import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "./HomePage.css";
import {
  getCourseInsights,
  getProducts,
  trackCourseSearch,
} from "../../api/productsApi";

import AboutSection from "../../components/AboutSection/AboutSection";
import CookieBanner from "../../components/CookieBanner/CookieBanner";
import CourseInsights from "../../components/CourseInsights/CourseInsights";
import CourseSearchResults from "../../components/CourseSearchResults/CourseSearchResults";
import CtaSection from "../../components/CtaSection/CtaSection";
import DeliverySection from "../../components/DeliverySection/DeliverySection";
import Footer from "../../components/Footer/Footer";
import Hero from "../../components/Hero/Hero";
import MarketingHighlights from "../../components/MarketingHighlights/MarketingHighlights";
import Navbar from "../../components/Navbar/Navbar";
import ProposalModal from "../../components/ProposalModal/ProposalModal";


/*
 * Determine how many courses should be retrieved based
 * on the current viewport width.
 *
 * >= 1800px : 20 courses - 5 columns x 4 rows
 * >= 1200px : 16 courses - 4 columns x 4 rows
 * >= 769px  :  9 courses - 3 columns x 3 rows
 * >= 601px  :  8 courses - 2 columns x 4 rows
 * <= 600px  :  6 courses - 1 column
 */
function getPageSize() {
  if (typeof window === "undefined") {
    return 6;
  }

  const width = window.innerWidth;

  if (width >= 1800) {
    return 20;
  }

  if (width >= 1200) {
    return 16;
  }

  if (width >= 769) {
    return 9;
  }

  if (width >= 601) {
    return 8;
  }

  return 6;
}


export default function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);

  const searchTerm = query.get("search") || "";
  const page = Math.max(1, Number(query.get("page")) || 1);


  /* -------------------------------------------------------
     State
     ------------------------------------------------------- */

  const [searchInput, setSearchInput] = useState(searchTerm);

  const [courses, setCourses] = useState([]);

  const [totalCourses, setTotalCourses] = useState(0);

  const [totalPages, setTotalPages] = useState(1);

  const [isLoading, setIsLoading] = useState(false);

  const [popularCourses, setPopularCourses] = useState([]);

  const [popularSearches, setPopularSearches] = useState([]);

  const [proposalCourse, setProposalCourse] = useState(null);

  const [pageSize, setPageSize] = useState(getPageSize);


  /* -------------------------------------------------------
     Keep search textbox synchronized with URL
     ------------------------------------------------------- */

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const sectionId = location.hash.replace("#", "");

    const scrollToSection = () => {
      const section = document.getElementById(sectionId);

      if (section) {
        section.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    };

    const timer = window.setTimeout(scrollToSection, 100);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.hash]);



  /* -------------------------------------------------------
     Responsive course page size

     This only changes pageSize when the viewport crosses
     one of our responsive breakpoints.
     ------------------------------------------------------- */

  useEffect(() => {
    const handleResize = () => {
      const nextPageSize = getPageSize();

      setPageSize((currentPageSize) => {
        if (currentPageSize === nextPageSize) {
          return currentPageSize;
        }

        return nextPageSize;
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);


  /* -------------------------------------------------------
     Load Courses
     ------------------------------------------------------- */

  useEffect(() => {
    let live = true;

    setIsLoading(true);

    getProducts(page, pageSize, searchTerm)
      .then((result) => {
        if (!live) {
          return;
        }

        const items =
          result?.data ||
          result?.courses ||
          result?.products ||
          [];

        const meta =
          result?.pagination ||
          result ||
          {};

        setCourses(items);

        setTotalCourses(
          meta.totalProducts ??
          meta.totalCourses ??
          items.length
        );

        setTotalPages(
          meta.totalPages ?? 1
        );

        if (searchTerm && items.length) {
          trackCourseSearch(
            items
              .map((item) => item._id)
              .filter(Boolean)
          ).catch(() => {});
        }
      })
      .catch(() => {
        if (!live) {
          return;
        }

        setCourses([]);

        setTotalCourses(0);

        setTotalPages(1);
      })
      .finally(() => {
        if (live) {
          setIsLoading(false);
        }
      });

    return () => {
      live = false;
    };
  }, [page, pageSize, searchTerm]);


  /* -------------------------------------------------------
     Load Popular Courses / Searches
     ------------------------------------------------------- */

  useEffect(() => {
    getCourseInsights()
      .then((data) => {
        setPopularCourses(
          data?.mostViewedCourses || []
        );

        setPopularSearches(
          data?.mostSearchedCourses || []
        );
      })
      .catch(() => {});
  }, []);


  /* -------------------------------------------------------
     Search
     ------------------------------------------------------- */

  const runSearch = (
    term = searchInput,
    nextPage = 1
  ) => {
    const value = term.trim();

    const route = value
      ? `/courses?search=${encodeURIComponent(
          value
        )}&page=${nextPage}`
      : `/courses?page=${nextPage}`;

    navigate(route);
  };


  /* -------------------------------------------------------
     Enter key search
     ------------------------------------------------------- */

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      runSearch();
    }
  };


  /* -------------------------------------------------------
     Render
     ------------------------------------------------------- */

  return (
    <main
      id="home-page"
      className="app-shell home-page"
    >
      <Navbar
        onRequestProposal={() =>
          setProposalCourse({})
        }
      />

      <Hero
        courseCount={totalCourses}
        searchInput={searchInput}
        searchTerm={searchTerm}
        onSearchInputChange={setSearchInput}
        onSearch={() => runSearch()}
        onClearSearch={() => runSearch("")}
        onSearchKeyDown={handleSearchKeyDown}
      />

      <MarketingHighlights />

      <AboutSection />

      <CourseInsights
        popularCourses={popularCourses}
        popularSearches={popularSearches}
        onCourseSelect={() => {}}
      />

      <CourseSearchResults
        courses={courses}
        totalCourses={totalCourses}
        currentPage={page}
        totalPages={totalPages}
        isLoading={isLoading}
        searchTerm={searchTerm}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={() => runSearch()}
        onSearchKeyDown={handleSearchKeyDown}
        onRequestQuote={setProposalCourse}
        onPageChange={(nextPage) =>
          runSearch(searchTerm, nextPage)
        }
      />

      <DeliverySection />

      <CtaSection
        onRequestProposal={() =>
          setProposalCourse({})
        }
      />

      <Footer
        onCourseFilter={(term) =>
          runSearch(term)
        }
      />

      <CookieBanner />

      <ProposalModal
        isOpen={proposalCourse !== null}
        selectedCourse={proposalCourse}
        onClose={() =>
          setProposalCourse(null)
        }
      />
    </main>
  );
}